import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Mail, MailOpen, Trash2, MessageCircle, Megaphone, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import ChatConversation from '../components/ChatConversation';
import OnlineUsers, { useOnlineIds } from '../components/OnlineUsers';

const API = process.env.REACT_APP_BACKEND_URL || '';

const MessagesPage = () => {
  const { user } = useContext(AuthContext);
  const onlineIds = useOnlineIds();
  const [conversations, setConversations] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [chatUser, setChatUser] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const isSystemMessage = (m) => m.is_broadcast || m.sender_id === 'system' || !m.sender_id;
  const systemMessages = inbox.filter(isSystemMessage);
  const systemUnread = systemMessages.filter((m) => !m.read).length;
  const chatUnread = conversations.reduce((sum, c) => sum + (c.unread || 0), 0);

  useEffect(() => {
    const chatId = searchParams.get('chat');
    if (chatId && user) {
      setChatUser({ id: chatId, name: searchParams.get('name') || '' });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, user, setSearchParams]);

  useEffect(() => {
    if (user) fetchMessages();
  }, [user]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [convRes, inboxRes] = await Promise.all([
        axios.get(`${API}/api/messages/conversations`, { headers }),
        axios.get(`${API}/api/messages/inbox`, { headers }),
      ]);
      setConversations(convRes.data.conversations || []);
      setInbox(inboxRes.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('שגיאה בטעינת הודעות');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSystemMessage = async (message) => {
    setSelectedMessage(message);
    setIsMessageOpen(true);
    if (!message.read && message.recipient_id === user.id) {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        await axios.put(`${API}/api/messages/${message.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInbox((prev) => prev.map((msg) => (msg.id === message.id ? { ...msg, read: true } : msg)));
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק הודעה זו?')) return;
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.delete(`${API}/api/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('ההודעה נמחקה בהצלחה');
      fetchMessages();
      setIsMessageOpen(false);
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('שגיאה במחיקת ההודעה');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.put(`${API}/api/messages/system/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('כל העדכונים סומנו כנקראו ✓');
      fetchMessages();
    } catch {
      toast.error('שגיאה בסימון ההודעות');
    }
  };

  const handleDeleteAllUpdates = async () => {
    if (!window.confirm('למחוק את כל העדכונים? פעולה זו לא ניתנת לביטול')) return;
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.delete(`${API}/api/messages/system/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('כל העדכונים נמחקו 🗑️');
      fetchMessages();
    } catch {
      toast.error('שגיאה במחיקת ההודעות');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatChatTime = (dateString) => {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
    }
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'אתמול';
    return d.toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric' });
  };

  const handleDeleteConversation = async (userId, name) => {
    if (!window.confirm(`למחוק את כל השיחה עם ${name}? הפעולה לא ניתנת לביטול`)) return;
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.delete(`${API}/api/messages/conversation/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('השיחה נמחקה 🗑️');
      fetchMessages();
    } catch {
      toast.error('שגיאה במחיקת השיחה');
    }
  };

  const ConversationRow = ({ convo }) => {
    const online = onlineIds.has(convo.user_id);
    const hasUnread = convo.unread > 0;
    return (
      <div className="relative group flex items-center">
        <button
          onClick={() => setChatUser({ id: convo.user_id, name: convo.name })}
          className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-slate-100 dark:hover:bg-slate-800/70 active:scale-[0.99] transition-all text-right"
          data-testid={`conversation-row-${convo.user_id}`}
        >
        <div className="relative shrink-0">
          <Avatar className="w-14 h-14 border border-slate-100 dark:border-slate-800 shadow-sm">
            {convo.avatar && <AvatarImage src={convo.avatar} alt={convo.name} />}
            <AvatarFallback className="bg-gradient-to-br from-[#1d6ff2] to-cyan-400 text-white font-bold text-lg">
              {convo.name?.charAt(0) || '?'}
            </AvatarFallback>
          </Avatar>
          {online && (
            <span className="absolute bottom-0.5 left-0.5 w-3.5 h-3.5 bg-[#10b47e] rounded-full border-2 border-white dark:border-slate-900" title="מחובר עכשיו"></span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={`truncate text-base ${hasUnread ? 'font-black text-[#1e2b5e] dark:text-white' : 'font-bold text-slate-800 dark:text-slate-100'}`}>
              {convo.name}
            </span>
            <span className={`text-[11px] shrink-0 ${hasUnread ? 'text-[#1d6ff2] font-bold' : 'text-slate-400'}`}>
              {formatChatTime(convo.last_at)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <span className={`truncate text-sm ${hasUnread ? 'font-bold text-slate-800 dark:text-slate-200' : 'text-slate-500 dark:text-slate-400'}`}>
              {convo.last_is_mine ? 'אתה: ' : ''}{convo.last_message}
            </span>
            {hasUnread && (
              <span className="shrink-0 min-w-[20px] h-5 px-1.5 bg-[#1d6ff2] text-white text-[11px] font-black rounded-full flex items-center justify-center" data-testid={`unread-badge-${convo.user_id}`}>
                {convo.unread > 99 ? '99+' : convo.unread}
              </span>
            )}
          </div>
        </div>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); handleDeleteConversation(convo.user_id, convo.name); }}
          className="shrink-0 p-2 ml-1 rounded-xl text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
          title="מחק שיחה"
          aria-label="מחק שיחה"
          data-testid={`delete-conversation-${convo.user_id}`}
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">טוען הודעות...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 brand-font" dir="rtl">
      <Navbar />

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-[#1e2b5e] dark:text-white mb-2 flex items-center justify-center gap-3" data-testid="messages-title">
            <MessageCircle className="w-9 h-9 text-[#1d6ff2]" />
            הודעות
          </h1>
        </div>

        {/* 🟢 מחוברים עכשיו */}
        <div className="mb-5">
          <OnlineUsers compact onUserClick={(u) => setChatUser({ id: u.id, name: u.full_name })} />
        </div>

        <Tabs defaultValue="chats" dir="rtl" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-5 rounded-2xl h-12 p-1">
            <TabsTrigger value="chats" className="flex items-center gap-2 rounded-xl font-bold" data-testid="tab-chats">
              <MessageCircle className="w-4 h-4" />
              צ'אטים
              {chatUnread > 0 && (
                <Badge className="bg-[#1d6ff2] text-white">{chatUnread}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="updates" className="flex items-center gap-2 rounded-xl font-bold" data-testid="tab-updates">
              <Megaphone className="w-4 h-4" />
              עדכונים
              {systemUnread > 0 && (
                <Badge className="bg-red-600 text-white">{systemUnread}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Chats - Messenger style */}
          <TabsContent value="chats">
            {conversations.length > 0 ? (
              <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm p-2 space-y-0.5" data-testid="conversations-list">
                {conversations.map((convo) => (
                  <ConversationRow key={convo.user_id} convo={convo} />
                ))}
              </div>
            ) : (
              <Card className="rounded-3xl">
                <CardContent className="py-12 text-center">
                  <MessageCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">אין שיחות עדיין</p>
                  <p className="text-gray-400 text-sm mt-1">לחץ על שחקן מחובר למעלה כדי להתחיל שיחה 🎾</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* System updates */}
          <TabsContent value="updates">
            {systemMessages.length > 0 && (
              <div className="flex items-center justify-end gap-2 mb-3">
                {systemUnread > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleMarkAllRead}
                    data-testid="mark-all-read-btn"
                    className="rounded-xl font-bold text-[#1d6ff2] border-[#1d6ff2]/30 hover:bg-[#1d6ff2]/10 flex items-center gap-1.5"
                  >
                    <CheckCheck className="w-4 h-4" />
                    סמן הכל כנקרא
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteAllUpdates}
                  data-testid="delete-all-updates-btn"
                  className="rounded-xl font-bold text-red-600 border-red-200 hover:bg-red-50 flex items-center gap-1.5"
                >
                  <Trash2 className="w-4 h-4" />
                  מחק הכל
                </Button>
              </div>
            )}
            {systemMessages.length > 0 ? (
              <div className="space-y-3">
                {systemMessages.map((message) => (
                  <Card
                    key={message.id}
                    className={`cursor-pointer rounded-2xl hover:shadow-lg transition-shadow ${!message.read ? 'border-[#1d6ff2] border-2' : ''}`}
                    onClick={() => handleOpenSystemMessage(message)}
                    data-testid={`system-message-${message.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {!message.read
                          ? <Mail className="w-5 h-5 text-[#1d6ff2] mt-1 shrink-0" />
                          : <MailOpen className="w-5 h-5 text-gray-400 mt-1 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold">{message.sender_name || 'מערכת'}</h3>
                            {!message.read && <Badge className="bg-[#1d6ff2]">חדש</Badge>}
                          </div>
                          <p className="font-medium text-gray-900 dark:text-slate-100 mb-1 truncate">{message.subject}</p>
                          <p className="text-sm text-gray-600 line-clamp-2">{message.content}</p>
                          <p className="text-xs text-gray-400 mt-2">{formatDate(message.created_at)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="rounded-3xl">
                <CardContent className="py-12 text-center">
                  <Megaphone className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">אין עדכוני מערכת</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Chat conversation dialog */}
      {chatUser && (
        <ChatConversation
          open={!!chatUser}
          onClose={() => { setChatUser(null); fetchMessages(); }}
          otherUserId={chatUser.id}
          otherUserName={chatUser.name}
          onActivity={fetchMessages}
        />
      )}

      {/* System Message View Dialog */}
      <Dialog open={isMessageOpen} onOpenChange={setIsMessageOpen}>
        <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden">
          {selectedMessage && (
            <>
              <div className="bg-gradient-to-br from-blue-600 via-cyan-500 to-emerald-500 px-6 py-5 text-white">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold tracking-widest text-white/80 uppercase mb-1">
                      {selectedMessage.is_broadcast ? '📢 הודעת מערכת' : '🤖 הודעה אוטומטית'}
                    </p>
                    <DialogTitle className="text-xl sm:text-2xl font-black text-white leading-tight tracking-tight">
                      {selectedMessage.subject}
                    </DialogTitle>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                </div>
                <DialogDescription className="text-white/90 text-sm flex items-center justify-between gap-2 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <span className="text-white/70">מאת:</span>
                    <span className="font-bold text-white">מערכת Tennis Pro Israel</span>
                  </span>
                  <span className="text-xs text-white/70 bg-white/10 px-2 py-1 rounded-full">
                    {formatDate(selectedMessage.created_at)}
                  </span>
                </DialogDescription>
              </div>
              <div className="px-6 py-5 bg-slate-50/50">
                <div
                  className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 whitespace-pre-wrap text-slate-800 leading-relaxed"
                  style={{ fontSize: '15px', lineHeight: '1.7' }}
                  dir="rtl"
                >
                  {selectedMessage.content}
                </div>
              </div>
              <div className="px-6 py-4 bg-white border-t border-slate-100 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsMessageOpen(false)} className="rounded-xl">
                  סגור
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteMessage(selectedMessage.id)}
                  className="flex items-center gap-2 rounded-xl"
                  data-testid="delete-message-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  מחק
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesPage;
